/**
 * Os campos fixos de cada cadastro (#568): os que o produto já tem e que um
 * campo personalizado não deve repetir.
 *
 * A tela só AVISA. O servidor não recusa o nome, porque "Plano" pode querer
 * dizer outra coisa numa revenda. Mas a clínica que cria um "Convênio" ao lado
 * do convênio da ficha passa a ter dois lugares para o mesmo dado, e ninguém
 * sabe qual vale.
 *
 * A lista é a da issue, cadastro por cadastro. Os da clínica só contam com a
 * clínica ligada.
 */

import type { EntidadeDeCampo } from '@cliqcrm/contracts';

interface CamposFixos {
  readonly todos: readonly string[];
  readonly daClinica: readonly string[];
}

export const CAMPOS_FIXOS: Readonly<Record<EntidadeDeCampo, CamposFixos>> = {
  cliente: {
    todos: ['Nome', 'Telefone', 'E-mail', 'Documento', 'CPF', 'CNPJ'],
    daClinica: [
      'Nome social',
      'Data de nascimento',
      'Nascimento',
      'CEP',
      'Endereço',
      'Logradouro',
      'Número',
      'Complemento',
      'Bairro',
      'Cidade',
      'UF',
      'Estado',
      'Profissão',
      'Estado civil',
      'Contato de emergência',
      'Telefone de emergência',
      'Canal preferido',
      'Horário preferido',
      'Convênio',
      'Operadora',
      'Plano',
      'Carteirinha',
      'Validade',
      'Titular',
      'Prontuário',
    ],
  },
  oportunidade: {
    todos: [
      'Nome',
      'Valor',
      'Funil',
      'Etapa',
      'Responsável',
      'Previsão de fechamento',
      'Motivo da perda',
      'Notas',
    ],
    daClinica: ['Médico', 'Especialidade', 'Data da consulta', 'Hora da consulta', 'Consulta'],
  },
  pedido_de_exame: {
    todos: [],
    daClinica: [
      'Exame',
      'Código do exame',
      'Código do pedido',
      'Laboratório',
      'Quem pediu',
      'Solicitante',
      'Urgente',
      'Observação',
      'Etapas',
    ],
  },
  tratamento: {
    todos: [],
    daClinica: [
      'Tratamento',
      'Código no SISAC',
      'Valor de tabela',
      'Valor negociado',
      'Fechado em',
      'Começa em',
      'Profissional',
      'Forma de pagamento',
      'Parcelas',
      'Sessões previstas',
      'Situação',
    ],
  },
};

/** Sem caixa, sem acento e com os espaços juntados, como o servidor compara. */
export function chaveDoNome(nome: string): string {
  return nome
    .trim()
    .replace(/\s+/g, ' ')
    .normalize('NFD')
    .replace(/\p{M}/gu, '')
    .toLocaleLowerCase('pt-BR');
}

/** O campo fixo que tem este nome, para o aviso, ou `null`. */
export function campoFixoComONome(
  entidade: EntidadeDeCampo,
  nome: string,
  clinica: boolean,
): string | null {
  const chave = chaveDoNome(nome);
  if (chave === '') return null;
  const { todos, daClinica } = CAMPOS_FIXOS[entidade];
  return (
    [...todos, ...(clinica ? daClinica : [])].find((fixo) => chaveDoNome(fixo) === chave) ?? null
  );
}
